import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ReactQuill from 'react-quill'
import DOMPurify from 'dompurify'
import 'react-quill/dist/quill.snow.css'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'
import { useAuth } from '../../context/AuthContext'
import {
  buildVideoEmbedUrl,
  createLessonRecord,
  createQuizForLesson,
  createLessonFromTemplate,
  createLessonTemplate,
  fetchLessonTemplates,
  fetchLessonsForCourse,
  uploadLessonAttachment,
  validateVideoUrl
} from '../../config/lessonService'
import { supabaseConfigError } from '../../config/supabase'

const {
  FiArrowLeft,
  FiBookOpen,
  FiCalendar,
  FiFilePlus,
  FiLink,
  FiPlus,
  FiSave,
  FiTrash2,
  FiType,
  FiUploadCloud,
  FiEye,
  FiEyeOff,
  FiClipboard,
  FiFilter,
  FiLayers,
  FiRefreshCw
} = FiIcons

const quizTypes = [
  { value: 'multiple_choice', label: 'Multiple choice' },
  { value: 'true_false', label: 'True/False' },
  { value: 'short_answer', label: 'Short answer' }
]

const LessonPreview = ({ lesson, quiz }) => {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-2">
          <span className="px-3 py-1 bg-[#00AAFF]/10 text-[#00AAFF] text-sm rounded-full font-medium">
            Day {lesson.day_number || '—'}
          </span>
          {lesson.subject && (
            <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
              {lesson.subject}
            </span>
          )}
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">{lesson.title || 'Untitled lesson'}</h2>
        {lesson.description && <p className="text-gray-600">{lesson.description}</p>}
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <SafeIcon icon={FiType} className="w-5 h-5 text-[#00AAFF]" />
          Lesson content
        </h3>
        <div className="prose max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(lesson.content || '<p>No content yet.</p>') }} />
      </div>

      {lesson.video_url && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <SafeIcon icon={FiLink} className="w-5 h-5 text-[#00AAFF]" />
            Video preview
          </h3>
          <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
            <iframe src={lesson.video_url} className="w-full h-full" title="Lesson video" allowFullScreen />
          </div>
        </div>
      )}

      {lesson.attachments?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <SafeIcon icon={FiUploadCloud} className="w-5 h-5 text-[#00AAFF]" />
            Attachments
          </h3>
          <div className="space-y-2">
            {lesson.attachments.map((file, index) => (
              <a
                key={`${file.path}-${index}`}
                href={file.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <div>
                  <p className="font-medium text-gray-800">{file.name}</p>
                  {file.description && <p className="text-sm text-gray-500">{file.description}</p>}
                </div>
                <span className="text-sm text-[#00AAFF]">Open</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {quiz?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <SafeIcon icon={FiClipboard} className="w-5 h-5 text-[#00AAFF]" />
            Quiz
          </h3>
          <div className="space-y-4">
            {quiz.map((question, index) => (
              <div key={question.id || index} className="p-4 border border-gray-200 rounded-lg">
                <p className="text-sm uppercase text-gray-500 font-semibold mb-1">{quizTypes.find((q) => q.value === question.type)?.label}</p>
                <p className="font-semibold text-gray-800 mb-2">{question.prompt || 'Untitled question'}</p>
                {question.type === 'multiple_choice' && (
                  <ul className="list-disc pl-5 space-y-1 text-gray-700">
                    {question.options?.map((option, optIndex) => (
                      <li key={optIndex} className={option.isCorrect ? 'font-semibold text-green-700' : ''}>
                        {option.text || 'Option'}
                        {option.isCorrect && ' (correct)'}
                      </li>
                    ))}
                  </ul>
                )}
                {question.type === 'true_false' && (
                  <p className="text-gray-700">Answer: {question.answer ? 'True' : 'False'}</p>
                )}
                {question.type === 'short_answer' && (
                  <p className="text-gray-700">Expected answer: {question.answer || 'Not set'}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const LessonAuthor = () => {
  const navigate = useNavigate()
  const { curriculumId, courseId } = useParams()
  const { user, userProfile } = useAuth()
  const [lessonValues, setLessonValues] = useState({
    title: '',
    description: '',
    subject: '',
    day_number: 1,
    order_index: 0,
    video_url: '',
    content: ''
  })
  const [attachments, setAttachments] = useState([])
  const [quizQuestions, setQuizQuestions] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [previewMode, setPreviewMode] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [templates, setTemplates] = useState([])
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [templateError, setTemplateError] = useState(null)
  const [templateSubjectFilter, setTemplateSubjectFilter] = useState('')
  const [templateSaving, setTemplateSaving] = useState(false)
  const [templateCreating, setTemplateCreating] = useState(false)
  const [creatingFromTemplateId, setCreatingFromTemplateId] = useState(null)

  const templateAccessAllowed = useMemo(
    () => ['parent', 'teacher'].includes(userProfile?.role),
    [userProfile?.role]
  )

  const loadTemplates = useCallback(
    async (subjectFilter = templateSubjectFilter) => {
      if (!userProfile || supabaseConfigError) return
      setTemplatesLoading(true)
      setTemplateError(null)
      try {
        const { data, error: fetchError } = await fetchLessonTemplates(subjectFilter.trim())
        if (fetchError) throw fetchError
        setTemplates(data || [])
      } catch (fetchErr) {
        console.error('Error loading templates', fetchErr)
        setTemplateError('Unable to load templates right now.')
      } finally {
        setTemplatesLoading(false)
      }
    },
    [userProfile, templateSubjectFilter]
  )

  const computedLesson = useMemo(() => ({
    ...lessonValues,
    attachments
  }), [lessonValues, attachments])

  useEffect(() => {
    const loadDefaultOrder = async () => {
      if (!courseId || supabaseConfigError) return
      setLoading(true)
      try {
        const { data, error: fetchError } = await fetchLessonsForCourse(courseId)
        if (fetchError) throw fetchError
        const nextOrder = data?.length ? data.length : 0
        setLessonValues((prev) => ({ ...prev, order_index: nextOrder }))
      } catch (loadError) {
        console.error('Error loading lessons for course', loadError)
      } finally {
        setLoading(false)
      }
    }
    loadDefaultOrder()
  }, [courseId])

  useEffect(() => {
    loadTemplates()
  }, [loadTemplates])

  const handleInputChange = (field, value) => {
    setLessonValues((prev) => ({ ...prev, [field]: value }))
  }

  const handleAddAttachment = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    setUploading(true)
    setError(null)
    const { data, error: uploadError } = await uploadLessonAttachment(file, courseId)
    if (uploadError) {
      console.error('Upload error', uploadError)
      setError('Could not upload file. Please try again.')
    } else if (data) {
      setAttachments((prev) => [...prev, { ...data, description: '' }])
    }
    setUploading(false)
    event.target.value = ''
  }

  const handleAttachmentDescriptionChange = (index, description) => {
    setAttachments((prev) =>
      prev.map((item, itemIndex) => (itemIndex === index ? { ...item, description } : item))
    )
  }

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, itemIndex) => itemIndex !== index))
  }

  const addQuizQuestion = () => {
    setQuizQuestions((prev) => [
      ...prev,
      {
        id: `question-${Date.now()}`,
        type: 'multiple_choice',
        prompt: '',
        options: [
          { text: 'Option 1', isCorrect: true },
          { text: 'Option 2', isCorrect: false }
        ],
        answer: ''
      }
    ])
  }

  const handleQuestionTypeChange = (index, newType) => {
    setQuizQuestions((prev) =>
      prev.map((question, qIndex) => {
        if (qIndex !== index) return question
        if (newType === 'multiple_choice') {
          return {
            ...question,
            type: newType,
            options: [
              { text: 'Option 1', isCorrect: true },
              { text: 'Option 2', isCorrect: false }
            ],
            answer: ''
          }
        }
        if (newType === 'true_false') {
          return { ...question, type: newType, answer: true, options: [] }
        }
        return { ...question, type: newType, answer: '', options: [] }
      })
    )
  }

  const updateQuizQuestion = (index, updates) => {
    setQuizQuestions((prev) => prev.map((question, qIndex) => (qIndex === index ? { ...question, ...updates } : question)))
  }

  const updateOption = (questionIndex, optionIndex, updates) => {
    setQuizQuestions((prev) =>
      prev.map((question, qIndex) => {
        if (qIndex !== questionIndex) return question
        const updatedOptions = question.options.map((opt, optIdx) => (optIdx === optionIndex ? { ...opt, ...updates } : opt))
        return { ...question, options: updatedOptions }
      })
    )
  }

  const markCorrectOption = (questionIndex, optionIndex) => {
    setQuizQuestions((prev) =>
      prev.map((question, qIndex) => {
        if (qIndex !== questionIndex) return question
        return {
          ...question,
          options: question.options.map((opt, optIdx) => ({
            ...opt,
            isCorrect: optIdx === optionIndex
          }))
        }
      })
    )
  }

  const addOptionToQuestion = (questionIndex) => {
    setQuizQuestions((prev) =>
      prev.map((question, qIndex) => {
        if (qIndex !== questionIndex) return question
        return {
          ...question,
          options: [...(question.options || []), { text: `Option ${question.options.length + 1}`, isCorrect: false }]
        }
      })
    )
  }

  const removeQuizQuestion = (index) => {
    setQuizQuestions((prev) => prev.filter((_, qIndex) => qIndex !== index))
  }

  const handleSave = async () => {
    if (supabaseConfigError) {
      setError(supabaseConfigError.message)
      return
    }
    if (!lessonValues.title) {
      setError('Please provide a lesson title.')
      return
    }
    if (!validateVideoUrl(lessonValues.video_url)) {
      setError('Please provide a valid YouTube or Vimeo video URL.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const payload = {
        title: lessonValues.title.trim(),
        description: lessonValues.description.trim(),
        subject: lessonValues.subject.trim(),
        day_number: Number(lessonValues.day_number) || 1,
        order_index: Number(lessonValues.order_index) || 0,
        content: lessonValues.content,
        video_url: buildVideoEmbedUrl(lessonValues.video_url.trim()),
        attachments,
        course_id: courseId,
        curriculum_id: curriculumId
      }

      const { data: lesson, error: lessonError } = await createLessonRecord(payload)
      if (lessonError) throw lessonError

      if (quizQuestions.length > 0) {
        const { error: quizError } = await createQuizForLesson(lesson.id, quizQuestions)
        if (quizError) throw quizError
      }

      navigate(`/lesson/${lesson.id}`)
    } catch (saveError) {
      console.error('Error saving lesson', saveError)
      setError('We could not save this lesson right now. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveTemplate = async () => {
    if (supabaseConfigError) {
      setTemplateError(supabaseConfigError.message)
      return
    }
    if (!templateAccessAllowed) {
      setTemplateError('Only parents and teachers can save templates.')
      return
    }
    if (!lessonValues.title || !lessonValues.subject) {
      setTemplateError('Please add a title and subject before saving as a template.')
      return
    }
    if (!validateVideoUrl(lessonValues.video_url)) {
      setTemplateError('Please provide a valid YouTube or Vimeo video URL.')
      return
    }
    setTemplateSaving(true)
    setTemplateError(null)
    try {
      const payload = {
        title: lessonValues.title.trim(),
        subject: lessonValues.subject.trim(),
        description: lessonValues.description?.trim() || '',
        content: lessonValues.content,
        media_url: buildVideoEmbedUrl(lessonValues.video_url.trim()),
        quiz_schema: quizQuestions,
        attachments_metadata: attachments,
        created_by: user?.id || null,
        created_role: userProfile?.role || null
      }
      const { error: templateSaveError } = await createLessonTemplate(payload)
      if (templateSaveError) throw templateSaveError
      await loadTemplates()
    } catch (templateSaveErr) {
      console.error('Error saving template', templateSaveErr)
      setTemplateError('Unable to save this template. Please try again.')
    } finally {
      setTemplateSaving(false)
    }
  }

  const handleApplyTemplate = (template) => {
    setLessonValues((prev) => ({
      ...prev,
      title: template.title || prev.title,
      subject: template.subject || prev.subject,
      description: template.description || '',
      content: template.content || '',
      video_url: template.media_url || '',
    }))
    setAttachments(template.attachments_metadata || [])
    setQuizQuestions(template.quiz_schema || [])
  }

  const handleCreateLessonFromTemplate = async (template) => {
    if (supabaseConfigError) {
      setError(supabaseConfigError.message)
      return
    }
    if (!templateAccessAllowed) {
      setError('Only parents and teachers can create lessons from templates.')
      return
    }
    setTemplateCreating(true)
    setCreatingFromTemplateId(template.id)
    setError(null)
    try {
      const { data, error: createError } = await createLessonFromTemplate(template.id, {
        course_id: courseId,
        curriculum_id: curriculumId,
        day_number: lessonValues.day_number,
        order_index: lessonValues.order_index
      })
      if (createError) throw createError
      navigate(`/lesson/${data.id}`)
    } catch (createErr) {
      console.error('Error creating lesson from template', createErr)
      setError('Could not create a lesson from this template right now.')
    } finally {
      setTemplateCreating(false)
      setCreatingFromTemplateId(null)
    }
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-[#00AAFF]"
        >
          <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
          Back
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={handleSaveTemplate}
            disabled={templateSaving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
          >
            <SafeIcon icon={FiLayers} className="w-4 h-4" />
            {templateSaving ? 'Saving template…' : 'Save as template'}
          </button>
          <button
            onClick={() => setPreviewMode((prev) => !prev)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50"
          >
            <SafeIcon icon={previewMode ? FiEyeOff : FiEye} className="w-4 h-4" />
            {previewMode ? 'Edit mode' : 'Preview'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#00AAFF] text-white hover:bg-[#0099E5] disabled:opacity-50"
          >
            <SafeIcon icon={FiSave} className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save lesson'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <SafeIcon icon={FiBookOpen} className="w-5 h-5 text-[#00AAFF]" />
          <div>
            <p className="text-sm text-gray-500">Curriculum #{curriculumId}</p>
            <p className="font-semibold text-gray-800">Course #{courseId} • Create lesson</p>
          </div>
        </div>

        {error && (
          <div className="m-4 p-3 rounded-lg bg-red-50 border border-red-100 text-red-700">
            {error}
          </div>
        )}

        {previewMode ? (
          <div className="p-6 bg-gray-50">
            <LessonPreview lesson={computedLesson} quiz={quizQuestions} />
          </div>
        ) : (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Lesson title</label>
                <input
                  type="text"
                  value={lessonValues.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="e.g., Exploring Fractions"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#00AAFF]"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Subject</label>
                <input
                  type="text"
                  value={lessonValues.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  placeholder="Math, Science, Reading..."
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#00AAFF]"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Day number</label>
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-500" />
                  <input
                    type="number"
                    min="1"
                    value={lessonValues.day_number}
                    onChange={(e) => handleInputChange('day_number', e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#00AAFF]"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Lesson order in course</label>
                <input
                  type="number"
                  min="0"
                  value={lessonValues.order_index}
                  onChange={(e) => handleInputChange('order_index', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#00AAFF]"
                />
                {loading && <p className="text-xs text-gray-500">Loading lesson order…</p>}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Description</label>
              <textarea
                value={lessonValues.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#00AAFF]"
                placeholder="Brief overview for learners"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <SafeIcon icon={FiType} className="w-4 h-4 text-gray-500" />
                Rich text content
              </label>
              <ReactQuill
                theme="snow"
                value={lessonValues.content}
                onChange={(content) => handleInputChange('content', content)}
                className="bg-white"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <SafeIcon icon={FiLink} className="w-4 h-4 text-gray-500" />
                Video URL (YouTube or Vimeo)
              </label>
              <input
                type="url"
                value={lessonValues.video_url}
                onChange={(e) => handleInputChange('video_url', e.target.value)}
                placeholder="https://youtube.com/..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#00AAFF]"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <SafeIcon icon={FiUploadCloud} className="w-4 h-4 text-gray-500" />
                  Attachments
                </p>
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer text-sm">
                  <SafeIcon icon={FiFilePlus} className="w-4 h-4" />
                  {uploading ? 'Uploading…' : 'Add file'}
                  <input type="file" className="hidden" onChange={handleAddAttachment} disabled={uploading} />
                </label>
              </div>
              {attachments.length === 0 ? (
                <p className="text-sm text-gray-500">No attachments yet.</p>
              ) : (
                <div className="space-y-3">
                  {attachments.map((file, index) => (
                    <div key={file.path} className="p-4 border border-gray-200 rounded-lg flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <p className="font-medium text-gray-800">{file.name}</p>
                        <input
                          type="text"
                          value={file.description}
                          onChange={(e) => handleAttachmentDescriptionChange(index, e.target.value)}
                          placeholder="Add a note about this file"
                          className="mt-2 w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#00AAFF]"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="p-2 rounded-lg hover:bg-red-50 text-red-600"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <SafeIcon icon={FiClipboard} className="w-4 h-4 text-gray-500" />
                  Quiz builder
                </p>
                <button
                  type="button"
                  onClick={addQuizQuestion}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm"
                >
                  <SafeIcon icon={FiPlus} className="w-4 h-4" />
                  Add question
                </button>
              </div>

              {quizQuestions.length === 0 ? (
                <p className="text-sm text-gray-500">No quiz questions yet.</p>
              ) : (
                <div className="space-y-4">
                  {quizQuestions.map((question, index) => (
                    <div key={question.id} className="p-4 border border-gray-200 rounded-xl space-y-3">
                      <div className="flex items-center justify-between">
                        <select
                          value={question.type}
                          onChange={(e) => handleQuestionTypeChange(index, e.target.value)}
                          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#00AAFF]"
                        >
                          {quizTypes.map((type) => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => removeQuizQuestion(index)}
                          className="p-2 rounded-lg hover:bg-red-50 text-red-600"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="space-y-2">
                        <label className="text-xs uppercase tracking-wide text-gray-500">Prompt</label>
                        <input
                          type="text"
                          value={question.prompt}
                          onChange={(e) => updateQuizQuestion(index, { prompt: e.target.value })}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#00AAFF]"
                          placeholder="Ask your question"
                        />
                      </div>

                      {question.type === 'multiple_choice' && (
                        <div className="space-y-2">
                          <p className="text-xs uppercase tracking-wide text-gray-500">Options</p>
                          <div className="space-y-2">
                            {question.options?.map((option, optIndex) => (
                              <div key={`${question.id}-option-${optIndex}`} className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name={`${question.id}-correct`}
                                  checked={option.isCorrect}
                                  onChange={() => markCorrectOption(index, optIndex)}
                                />
                                <input
                                  type="text"
                                  value={option.text}
                                  onChange={(e) => updateOption(index, optIndex, { text: e.target.value })}
                                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#00AAFF]"
                                  placeholder={`Option ${optIndex + 1}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => markCorrectOption(index, optIndex)}
                                  className="text-xs text-[#00AAFF] hover:text-[#0099E5]"
                                >
                                  Mark correct
                                </button>
                              </div>
                            ))}
                          </div>
                          <button
                            type="button"
                            onClick={() => addOptionToQuestion(index)}
                            className="text-sm text-[#00AAFF] hover:text-[#0099E5]"
                          >
                            Add option
                          </button>
                        </div>
                      )}

                      {question.type === 'true_false' && (
                        <div className="space-y-2">
                          <label className="text-xs uppercase tracking-wide text-gray-500">Answer</label>
                          <select
                            value={question.answer ? 'true' : 'false'}
                            onChange={(e) => updateQuizQuestion(index, { answer: e.target.value === 'true' })}
                            className="border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#00AAFF]"
                          >
                            <option value="true">True</option>
                            <option value="false">False</option>
                          </select>
                        </div>
                      )}

                      {question.type === 'short_answer' && (
                        <div className="space-y-2">
                          <label className="text-xs uppercase tracking-wide text-gray-500">Expected answer</label>
                          <input
                            type="text"
                            value={question.answer}
                            onChange={(e) => updateQuizQuestion(index, { answer: e.target.value })}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#00AAFF]"
                            placeholder="Learner response you expect"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiLayers} className="w-4 h-4 text-gray-500" />
                  <p className="text-sm font-medium text-gray-700">Templates (parent/teacher only)</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg bg-white">
                    <SafeIcon icon={FiFilter} className="w-4 h-4 text-gray-500" />
                    <input
                      type="text"
                      value={templateSubjectFilter}
                      onChange={(e) => setTemplateSubjectFilter(e.target.value)}
                      placeholder="Filter by subject"
                      className="outline-none text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => loadTemplates()}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm"
                  >
                    <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${templatesLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              </div>

              {templateError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-100 text-red-700 text-sm">
                  {templateError}
                </div>
              )}

              {!templateAccessAllowed && (
                <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-100 text-yellow-800 text-sm">
                  Templates are available to parent and teacher accounts.
                </div>
              )}

              {templatesLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[1, 2, 3].map((skeleton) => (
                    <div key={skeleton} className="h-24 rounded-xl bg-gray-100 animate-pulse" />
                  ))}
                </div>
              ) : templates.length === 0 ? (
                <div className="p-4 border border-dashed border-gray-200 rounded-xl text-sm text-gray-600">
                  No templates found for this subject filter.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {templates.map((template) => (
                    <div key={template.id} className="border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs uppercase tracking-wide text-gray-500">{template.subject}</p>
                        <span className="text-xs text-gray-500">
                          {template.created_at ? new Date(template.created_at).toLocaleDateString() : '—'}
                        </span>
                      </div>
                      <h4 className="font-semibold text-gray-900">{template.title}</h4>
                      {template.description && <p className="text-sm text-gray-600 line-clamp-2">{template.description}</p>}
                      <div className="flex items-center gap-2 pt-2">
                        <button
                          type="button"
                          onClick={() => handleApplyTemplate(template)}
                          className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-100 text-sm"
                        >
                          Use in form
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCreateLessonFromTemplate(template)}
                          disabled={templateCreating && creatingFromTemplateId === template.id}
                          className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#00AAFF] text-white hover:bg-[#0099E5] disabled:opacity-50 text-sm"
                        >
                          {templateCreating && creatingFromTemplateId === template.id ? 'Creating…' : 'Create lesson'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default LessonAuthor
